import { useEffect } from "react";
import { Navigate } from "react-router";

import { useNonModalLocation } from "../Utils/routing";
import { ALERT_TYPE_ERROR } from "../Alerts/Alert";
import { useAlert } from "../Alerts/withAlert";
import { persistor } from "../../config/reduxStore";

// Give <Navigate> time to perform the navigation before falling back.
const NAVIGATION_FALLBACK_DELAY = 300;

// Authorization notices can be raised while rendering a modal route. The normal path is
// a SPA navigation back to the last non-modal page, or the closest matching parent route
// for direct links.
//
// The hard-navigation fallback is intentionally scheduled during render. In the broken
// cold-load case this error fallback is not committed, so useEffect never runs and can't
// be used as the escape hatch. Replayed renders may schedule duplicate timers, but the
// same-path guard makes them no-ops once navigation has happened.
const NavigateToNonModalLocation = ({ notice }) => {
  const { pathname } = useNonModalLocation();
  const { showAlert } = useAlert();

  const fallbackTimeout = setTimeout(async () => {
    if (window.location.pathname === pathname) return;

    if (notice) showAlert({ type: ALERT_TYPE_ERROR, message: notice });

    // The fallback uses a full reload, so flush redux-persist first to keep the
    // authorization notice visible on the target page.
    await persistor.flush();
    window.location.replace(pathname);
  }, NAVIGATION_FALLBACK_DELAY);

  // Committed renders don't need the fallback: <Navigate> performs the navigation.
  // If this fallback render is discarded, this effect never runs and the timer remains.
  useEffect(() => {
    clearTimeout(fallbackTimeout);
  }, [fallbackTimeout]);

  return <Navigate to={pathname} replace={true} />;
};

export default NavigateToNonModalLocation;
